import { concat, Observable, UnaryFunction } from 'rxjs';
import { skip, take } from 'rxjs/operators';

/**
 * An RxJS operator that skips applying the wrapped operators to the first emission.
 * Useful for avoiding debounce delays on initial values while still debouncing subsequent changes.
 *
 * Usage:
 * ```ts
 * observable$.pipe(
 *   afterInitial(
 *     debounceTime(50),
 *     delay(100)
 *   )
 * )
 * ```
 *
 * @param operators The operators to apply only after the first emission
 * @returns A unary function that applies the operators after the initial emission
 */
export function rxAfterInitial<T>(
  ...operators: UnaryFunction<Observable<T>, Observable<T>>[]
): UnaryFunction<Observable<T>, Observable<T>> {
  return (source: Observable<T>) => {
    // Split the source into first emission and subsequent emissions
    const firstEmission$ = source.pipe(take(1));

    // Apply operators to subsequent emissions by chaining them
    let subsequentEmissions$ = source.pipe(skip(1));
    for (const operator of operators) {
      subsequentEmissions$ = subsequentEmissions$.pipe(operator);
    }

    // Concat the first emission (without operators) with subsequent emissions (with operators)
    return concat(firstEmission$, subsequentEmissions$);
  };
}
